import React, { useEffect, useState } from "react";
import { EuiSuperDatePicker, EuiThemeProvider } from "@elastic/eui";
import { useTheme } from "../../context/ThemeContext";
import { FiAlertCircle, FiDownload, FiLoader } from "react-icons/fi";
import { Tooltip } from "react-tooltip";

const CdrFilters = ({
  setFilters,
  downloadCsv,
  downloadAgentSessionsCsv,
}) => {
  const { isDarkMode } = useTheme();

  const isMoovecars =
    (localStorage.getItem("CN") || "") === "MooveCars";

  const [localFilters, setLocalFilters] = useState({
    phone: "",
    direction: ["Inbound", "Outbound"],
    callResult: null,
    queue: "",
    originCountry: "",
    startDate: "now-1d",
    endDate: "now",
  });

  const [loadingCsv, setLoadingCsv] = useState(false);
  const [csvError, setCsvError] = useState(null);

  const [loadingAgentSessionsCsv, setLoadingAgentSessionsCsv] =
    useState(false);

  const [agentSessionsCsvError, setAgentSessionsCsvError] =
    useState(null);

  const [initialFilters] = useState({
    ...localFilters,
  });

  const [appliedFilters, setAppliedFilters] = useState({
    ...localFilters,
  });

  const callResultOptions = [
    {
      value: "Attended",
      label: "Attended",
    },
    {
      value: "Abandon",
      label: "Abandon",
    },
    {
      value: "Timeout",
      label: "Timeout",
    },
    {
      value: "Canceled",
      label: "Canceled",
    },
    {
      value: "Non Attended",
      label: "Non Attended",
    },
  ];

  /*
   * Convierte los filtros locales al formato que espera
   * el backend.
   */
  const buildFiltersPayload = (sourceFilters = localFilters) => {
    return {
      ...sourceFilters,

      direction:
        sourceFilters.direction.length === 2
          ? null
          : sourceFilters.direction.length === 1
          ? sourceFilters.direction[0]
          : null,
    };
  };

  /*
   * ¿Hay cambios pendientes de aplicar?
   */
  const hasChanges =
    JSON.stringify(localFilters) !==
    JSON.stringify(appliedFilters);

  /*
   * ¿Estamos en el estado inicial?
   */
  const isInitialState =
    JSON.stringify(localFilters) ===
    JSON.stringify(initialFilters);

  /*
   * Inputs normales
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setLocalFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /*
   * Direction
   */
  const handleCheckboxChange = (value) => {
    setLocalFilters((prev) => {
      const newDirection = prev.direction.includes(value)
        ? prev.direction.filter(
            (dir) => dir !== value
          )
        : [...prev.direction, value];

      return {
        ...prev,
        direction: newDirection,
      };
    });
  };

  /*
   * Selects
   */
  const handleSelectChange = (name, option) => {
    setLocalFilters((prev) => ({
      ...prev,
      [name]: option,
    }));
  };

  /*
   * Fechas
   */
  const handleDateChange = ({
    start,
    end,
  }) => {
    setLocalFilters((prev) => ({
      ...prev,
      startDate: start,
      endDate: end,
    }));
  };

  /*
   * =====================================================
   * BÚSQUEDA DINÁMICA DE PHONE / EXTENSION
   * =====================================================
   *
   * - 0 caracteres:
   *   elimina el filtro phone y vuelve a buscar.
   *
   * - 1 o 2 caracteres:
   *   no hace ninguna petición.
   *
   * - 3 o más:
   *   espera 350ms y lanza automáticamente la búsqueda.
   *
   * El timeout anterior se cancela si el usuario sigue
   * escribiendo.
   */
  useEffect(() => {
    const phone = String(
      localFilters.phone || ""
    ).trim();

    /*
     * Con 1 o 2 caracteres no buscamos.
     */
    if (
      phone.length > 0 &&
      phone.length < 3
    ) {
      return;
    }

    const timer = setTimeout(() => {
      const filtersToApply =
        buildFiltersPayload({
          ...localFilters,
          phone,
        });

      /*
       * Aplicamos filtros al padre.
       *
       * CdrMain volverá automáticamente a page 1
       * porque applyFilters() ya hace ese reset.
       */
      setFilters(filtersToApply);

      /*
       * Actualizamos el último estado aplicado.
       *
       * Esto evita que aparezca continuamente
       * "Apply Filters" únicamente por el phone.
       */
      setAppliedFilters((prev) => ({
        ...prev,
        phone,
      }));
    }, 350);

    return () => {
      clearTimeout(timer);
    };
  }, [localFilters.phone]);

  /*
   * Descargar CSV
   */
  const handleDownloadCsv = async () => {
    setLoadingCsv(true);
    setCsvError(null);

    try {
      await downloadCsv();
    } catch (err) {
      setCsvError(
        "Failed to download CSV."
      );

      console.error(err);
    } finally {
      setLoadingCsv(false);
    }
  };

  /*
   * Descargar sesiones
   */
  const handleDownloadAgentSessionsCsv =
    async () => {
      setLoadingAgentSessionsCsv(true);
      setAgentSessionsCsvError(null);

      try {
        await downloadAgentSessionsCsv();
      } catch (err) {
        setAgentSessionsCsvError(
          "Failed to download Agent Sessions CSV."
        );

        console.error(err);
      } finally {
        setLoadingAgentSessionsCsv(false);
      }
    };

  /*
   * Aplicar / limpiar filtros manualmente.
   */
  const handleButtonClick = () => {
    if (hasChanges) {
      const filters =
        buildFiltersPayload();

      setFilters(filters);

      setAppliedFilters({
        ...localFilters,
      });
    } else if (!isInitialState) {
      const resetFilters = {
        ...initialFilters,
        direction: [
          "Inbound",
          "Outbound",
        ],
      };

      setLocalFilters(
        resetFilters
      );

      setFilters(
        buildFiltersPayload(
          resetFilters
        )
      );

      setAppliedFilters(
        resetFilters
      );
    }
  };

  /*
   * Tema EUI
   */
  useEffect(() => {
    const themeLight = "/white.css";
    const themeDark = "/dark.css";

    const loadTheme = (
      themePath
    ) => {
      const head =
        document.head;

      const link =
        document.createElement(
          "link"
        );

      link.type =
        "text/css";

      link.rel =
        "stylesheet";

      link.href =
        themePath;

      const existingLink =
        document.getElementById(
          "theme-style"
        );

      if (existingLink) {
        head.removeChild(
          existingLink
        );
      }

      link.id =
        "theme-style";

      head.appendChild(
        link
      );
    };

    if (isDarkMode) {
      loadTheme(
        themeDark
      );
    } else {
      loadTheme(
        themeLight
      );
    }

    return () => {
      const existingLink =
        document.getElementById(
          "theme-style"
        );

      if (existingLink) {
        document.head.removeChild(
          existingLink
        );
      }
    };
  }, [isDarkMode]);

  return (
    <div className="w-full flex justify-between items-end gap-4">
      {/* Inputs y Selects */}
      <div className="flex flex-wrap gap-4 items-end">
        {/* PHONE / EXTENSION */}
        <div className="flex flex-col w-[220px]">
          <label className="text-xs font-semibold mb-1">
            Phone / extension
          </label>

          <input
            type="text"
            name="phone"
            inputMode="tel"
            autoComplete="off"
            placeholder="e.g., 678123456 or 202"
            value={
              localFilters.phone
            }
            onChange={
              handleInputChange
            }
            className="
              h-[40px]
              px-3
              border
              rounded-md
              text-sm
              shadow-md
              focus:ring
              focus:ring-blue-200
              bg-white
              dark:bg-primary-dark
              text-gray-700
              dark:text-gray-300
              border-gray-300
              dark:border-gray-600
            "
          />
        </div>

        {/* DIRECTION */}
        <div className="flex flex-col w-[150px]">
          <label className="text-xs font-semibold mb-1">
            Direction
          </label>

          <div className="flex items-center gap-4 h-[40px]">
            <label className="flex items-center text-sm">
              <input
                type="checkbox"
                checked={localFilters.direction.includes(
                  "Inbound"
                )}
                onChange={() =>
                  handleCheckboxChange(
                    "Inbound"
                  )
                }
                className="
                  mr-2
                  shadow-md
                  focus:ring
                  focus:ring-blue-200
                  bg-white
                  dark:bg-primary-dark
                  text-gray-700
                  dark:text-gray-300
                  border-gray-300
                  dark:border-gray-600
                "
              />

              Inbound
            </label>

            <label className="flex items-center text-sm">
              <input
                type="checkbox"
                checked={localFilters.direction.includes(
                  "Outbound"
                )}
                onChange={() =>
                  handleCheckboxChange(
                    "Outbound"
                  )
                }
                className="
                  mr-2
                  shadow-md
                  focus:ring
                  focus:ring-blue-200
                  bg-white
                  dark:bg-primary-dark
                  text-gray-700
                  dark:text-gray-300
                  border-gray-300
                  dark:border-gray-600
                "
              />

              Outbound
            </label>
          </div>
        </div>

        {/* CALL RESULT */}
        <div className="flex flex-col w-[150px]">
          <label className="text-xs font-semibold mb-1">
            Call Result
          </label>

          <select
            name="callResult"
            value={
              localFilters.callResult ||
              ""
            }
            onChange={(e) =>
              handleSelectChange(
                "callResult",
                e.target.value ||
                  null
              )
            }
            className="
              h-[40px]
              px-3
              border
              rounded-md
              text-sm
              shadow-md
              focus:ring
              focus:ring-blue-200
              bg-white
              dark:bg-primary-dark
              text-gray-700
              dark:text-gray-300
              border-gray-300
              dark:border-gray-600
            "
          >
            <option value="">
              All
            </option>

            {callResultOptions.map(
              (option) => (
                <option
                  key={
                    option.value
                  }
                  value={
                    option.value
                  }
                >
                  {
                    option.label
                  }
                </option>
              )
            )}
          </select>
        </div>

        {/* ORIGIN COUNTRY - SOLO MOOVECARS */}
        {isMoovecars && (
          <div className="flex flex-col w-[150px]">
            <label className="text-xs font-semibold mb-1">
              Origin country
            </label>

            <select
              name="originCountry"
              value={localFilters.originCountry || ""}
              onChange={(e) =>
                handleSelectChange(
                  "originCountry",
                  e.target.value || ""
                )
              }
              className="
                h-[40px]
                px-3
                border
                rounded-md
                text-sm
                shadow-md
                focus:ring
                focus:ring-blue-200
                bg-white
                dark:bg-primary-dark
                text-gray-700
                dark:text-gray-300
                border-gray-300
                dark:border-gray-600
              "
            >
              <option value="">All</option>
              <option value="INTERNATIONAL">International</option>
              <option value="ES">Spain</option>
              <option value="IT">Italy</option>
              <option value="FR">France</option>
              <option value="NL">Netherlands</option>
              <option value="US">United States</option>
            </select>
          </div>
        )}

        {/* QUEUE */}
        <div className="flex flex-col w-[150px]">
          <label className="text-xs font-semibold mb-1">
            Queue
          </label>

          <input
            type="text"
            name="queue"
            placeholder="Queue Name"
            value={
              localFilters.queue
            }
            onChange={
              handleInputChange
            }
            className="
              h-[40px]
              px-3
              border
              rounded-md
              text-sm
              shadow-md
              focus:ring
              focus:ring-blue-200
              bg-white
              dark:bg-primary-dark
              text-gray-700
              dark:text-gray-300
              border-gray-300
              dark:border-gray-600
            "
          />
        </div>
      </div>

      {/* Date Picker y botones */}
      <div className="flex items-end gap-4">
        {/* EXPORT CSV */}
        <button
          onClick={
            handleDownloadCsv
          }
          className={`flex relative h-[40px] px-4 rounded-md text-sm font-medium shadow-md items-center gap-2 ${
            loadingCsv
              ? "bg-gray-500 cursor-not-allowed"
              : "bg-[#54b054] text-white hover:bg-[#107c10]"
          }`}
          disabled={
            loadingCsv
          }
        >
          {loadingCsv ? (
            <>
              <FiLoader className="animate-spin" />
              Downloading...
            </>
          ) : (
            <>
              <FiDownload />
              Export CSV
            </>
          )}

          {csvError && (
            <>
              <FiAlertCircle
                data-tooltip-id="csv-error-tooltip"
                data-tooltip-content={
                  csvError
                }
                className="absolute top-0 right-0 -mt-2 -mr-2 text-red-500"
              />

              <Tooltip
                id="csv-error-tooltip"
                place="left"
              />
            </>
          )}
        </button>

        {/* DATE PICKER */}
        <EuiThemeProvider
          colorMode={
            isDarkMode
              ? "dark"
              : "light"
          }
        >
          <EuiSuperDatePicker
            start={
              localFilters.startDate
            }
            end={
              localFilters.endDate
            }
            onTimeChange={
              handleDateChange
            }
            showUpdateButton={
              false
            }
            className="w-auto shadow-md"
          />
        </EuiThemeProvider>

        {/* APPLY / CLEAR */}
        <button
          onClick={
            handleButtonClick
          }
          className={`h-[40px] px-4 rounded-md text-sm font-medium shadow-md ${
            hasChanges
              ? "bg-blue-600 text-white hover:bg-blue-700"
              : !isInitialState
              ? "bg-red-600 text-white hover:bg-red-700"
              : "bg-gray-300 text-gray-500 cursor-not-allowed"
          }`}
          disabled={
            isInitialState &&
            !hasChanges
          }
        >
          {hasChanges
            ? "Apply Filters"
            : "Clear Filters"}
        </button>

        {/* AGENT SESSIONS CSV */}
        {isMoovecars && (
          <button
            onClick={
              handleDownloadAgentSessionsCsv
            }
            className={`flex relative h-[40px] px-4 rounded-md text-sm font-medium shadow-md items-center gap-2 ${
              loadingAgentSessionsCsv
                ? "bg-gray-500 cursor-not-allowed text-white"
                : "bg-[#1d70b8] text-white hover:bg-[#155a96]"
            }`}
            disabled={
              loadingAgentSessionsCsv
            }
          >
            {loadingAgentSessionsCsv ? (
              <>
                <FiLoader className="animate-spin" />
                Downloading...
              </>
            ) : (
              <>
                <FiDownload />
                Agent Sessions CSV
              </>
            )}

            {agentSessionsCsvError && (
              <>
                <FiAlertCircle
                  data-tooltip-id="agent-sessions-csv-error-tooltip"
                  data-tooltip-content={
                    agentSessionsCsvError
                  }
                  className="absolute top-0 right-0 -mt-2 -mr-2 text-red-500"
                />

                <Tooltip
                  id="agent-sessions-csv-error-tooltip"
                  place="left"
                />
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default CdrFilters;
